(() => {
  if (window.__collectionInfiniteScrollLoaded) {
    return;
  }
  window.__collectionInfiniteScrollLoaded = true;

  const SELECTORS = {
    collection: '.collection',
    productList: 'product-list, .product-list',
    pagination: '.pagination',
    nextLink: 'a[rel="next"], a.pagination__next, .pagination__item--next a'
  };

  const instances = window.__collectionInfiniteScrollInstances || (window.__collectionInfiniteScrollInstances = new WeakMap());

  class CollectionInfiniteScroll {
    constructor(collection) {
      this.collection = collection;
      this.productList = collection.querySelector(SELECTORS.productList);
      this.pagination = collection.querySelector(SELECTORS.pagination);
      this.nextUrl = this.getNextUrl(this.pagination);
      this.loading = false;
      this.observer = null;
      this.sentinel = this.createSentinel();
      this.status = this.createStatusElement();
    }

    get isEligible() {
      return Boolean(this.collection && this.productList && this.pagination && this.nextUrl);
    }

    init() {
      if (!this.isEligible) {
        return;
      }

      this.hidePagination();
      this.insertHelpers();
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        rootMargin: '400px 0px'
      });
      this.observer.observe(this.sentinel);
    }

    destroy() {
      this.observer?.disconnect();
      this.observer = null;
      this.sentinel.remove();
      this.status.remove();
      this.showPagination();
    }

    createStatusElement() {
      const status = document.createElement('div');
      status.className = 'collection-infinite-scroll__status';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      status.hidden = true;
      status.textContent = window.themeVariables?.strings?.loading || 'Chargement...';
      status.style.textAlign = 'center';
      status.style.margin = 'var(--spacing-6, 3rem) auto';
      return status;
    }

    createSentinel() {
      const sentinel = document.createElement('div');
      sentinel.className = 'collection-infinite-scroll__sentinel';
      sentinel.setAttribute('aria-hidden', 'true');
      sentinel.style.width = '100%';
      sentinel.style.height = '1px';
      sentinel.style.marginTop = '1px';
      return sentinel;
    }

    insertHelpers() {
      const parent = this.productList.parentElement;
      if (!parent) {
        return;
      }
      parent.querySelectorAll('.collection-infinite-scroll__status').forEach((el) => {
        if (el !== this.status) {
          el.remove();
        }
      });
      parent.querySelectorAll('.collection-infinite-scroll__sentinel').forEach((el) => {
        if (el !== this.sentinel) {
          el.remove();
        }
      });
      if (!this.status.isConnected) {
        if (!this.productList.nextSibling) {
          parent.append(this.status);
        } else {
          parent.insertBefore(this.status, this.productList.nextSibling);
        }
      }
      if (!this.sentinel.isConnected) {
        this.status.insertAdjacentElement('afterend', this.sentinel);
      }
    }

    hidePagination() {
      if (!this.pagination) {
        return;
      }
      this.pagination.setAttribute('data-infinite-scroll-hidden', 'true');
      this.pagination.style.display = 'none';
    }

    showPagination() {
      if (!this.pagination) {
        return;
      }
      this.pagination.removeAttribute('data-infinite-scroll-hidden');
      this.pagination.style.display = '';
    }

    async onIntersect(entries) {
      if (!entries.some((entry) => entry.isIntersecting)) {
        return;
      }
      await this.loadMore();
    }

    async loadMore() {
      if (this.loading || !this.nextUrl) {
        return;
      }

      this.loading = true;
      this.status.hidden = false;
      this.status.setAttribute('aria-busy', 'true');
      this.status.textContent = window.themeVariables?.strings?.loading || 'Chargement...';

      const urlToLoad = this.nextUrl;

      try {
        const response = await fetch(urlToLoad, {
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          throw new Error(response.statusText);
        }

        const html = await response.text();
        this.appendContentFromResponse(html, urlToLoad);
      } catch (error) {
        console.error('Collection infinite scroll error:', error);
        this.status.textContent = 'Impossible de charger plus de produits pour le moment.';
        this.observer?.disconnect();
      } finally {
        this.status.removeAttribute('aria-busy');
        this.loading = false;
      }
    }

    appendContentFromResponse(html, loadedUrl) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const newCollection = doc.querySelector(SELECTORS.collection);
      if (!newCollection) {
        this.disable();
        return;
      }

      const newProductList = newCollection.querySelector(SELECTORS.productList);
      if (newProductList && newProductList.children.length > 0) {
        const fragment = document.createDocumentFragment();
        const appendedNodes = [];
        Array.from(newProductList.children).forEach((child) => {
          if (child.tagName === 'SCRIPT') {
            return;
          }
          appendedNodes.push(child);
          fragment.appendChild(child);
        });
        this.productList.appendChild(fragment);
        this.animateNewProducts(appendedNodes);
        window.Shopify?.loadFeatures?.([]);
        document.dispatchEvent(new CustomEvent('collection:infinite-scroll:loaded', {
          bubbles: true,
          detail: { url: loadedUrl }
        }));
      }

      const incomingPagination = newCollection.querySelector(SELECTORS.pagination);
      if (incomingPagination) {
        this.pagination.innerHTML = incomingPagination.innerHTML;
      }

      this.nextUrl = this.getNextUrl(incomingPagination);
      if (this.nextUrl) {
        this.status.hidden = true;
        this.observeSentinel();
      } else {
        this.status.textContent = window.themeVariables?.strings?.noMoreProducts || 'Tous les produits ont été chargés.';
        this.status.hidden = false;
        this.observer?.disconnect();
      }

      this.updateHistory(loadedUrl);
    }

    animateNewProducts(nodes) {
      if (!Array.isArray(nodes) || nodes.length === 0) {
        return;
      }

      const elementsToAnimate = nodes.flatMap((node) => {
        if (!(node instanceof Element)) {
          return [];
        }
        if (node.matches('[reveal-on-scroll="true"]')) {
          return [node];
        }
        return Array.from(node.querySelectorAll('[reveal-on-scroll="true"]'));
      });

      if (elementsToAnimate.length === 0) {
        return;
      }

      const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
      if (prefersReducedMotion) {
        elementsToAnimate.forEach((element) => {
          element.removeAttribute('reveal-on-scroll');
        });
        return;
      }

      const shouldStagger = Boolean(window.themeVariables?.settings?.staggerProducts);
      const baseDelay = shouldStagger ? 100 : 0;
      const stepDelay = shouldStagger ? 50 : 0;

      elementsToAnimate.forEach((element, index) => {
        if (!element || typeof element.animate !== 'function') {
          element?.removeAttribute('reveal-on-scroll');
          return;
        }

        const animation = element.animate(
          [
            { opacity: 0, transform: 'translateY(20px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          {
            duration: 100,
            easing: 'ease-in-out',
            delay: baseDelay + index * stepDelay,
            fill: 'forwards'
          }
        );

        const handleAnimationEnd = () => {
          element.style.opacity = '';
          element.style.transform = '';
          element.removeAttribute('reveal-on-scroll');
          animation.removeEventListener('finish', handleAnimationEnd);
          animation.removeEventListener('cancel', handleAnimationEnd);
        };

        animation.addEventListener('finish', handleAnimationEnd);
        animation.addEventListener('cancel', handleAnimationEnd);
      });
    }

    observeSentinel() {
      if (!this.observer) {
        this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
          rootMargin: '400px 0px'
        });
      }
      this.observer.observe(this.sentinel);
    }

    disable() {
      this.observer?.disconnect();
      this.status.textContent = '';
      this.status.hidden = true;
      this.showPagination();
    }

    updateHistory(url) {
      if (!url) {
        return;
      }
      const newUrl = new URL(url, window.location.href);
      window.history.replaceState(window.history.state, document.title, newUrl.toString());
    }

    getNextUrl(pagination) {
      if (!pagination) {
        return null;
      }
      const link = pagination.querySelector(SELECTORS.nextLink);
      if (!link) {
        return null;
      }
      return new URL(link.getAttribute('href'), window.location.href).toString();
    }
  }

  function initCollections(root = document, { force = false } = {}) {
    const collections = root.querySelectorAll(SELECTORS.collection);
    collections.forEach((collection) => {
      if (instances.has(collection)) {
        if (!force) {
          return;
        }
        instances.get(collection).destroy();
        instances.delete(collection);
      }
      const instance = new CollectionInfiniteScroll(collection);
      if (instance.isEligible) {
        instance.init();
        instances.set(collection, instance);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initCollections();
    });
  } else {
    initCollections();
  }

  document.addEventListener('theme:loading:end', () => {
    requestAnimationFrame(() => initCollections());
  });

  document.addEventListener('shopify:section:load', (event) => {
    initCollections(event.target, { force: true });
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const collections = event.target.querySelectorAll(SELECTORS.collection);
    collections.forEach((collection) => {
      const instance = instances.get(collection);
      if (instance) {
        instance.destroy();
        instances.delete(collection);
      }
    });
  });
})();